export type Span = { name: string; start: bigint; end?: bigint };

// Lightweight per-request timer to emit Server-Timing header and quick logs
export class SsrTimer {
  private spans: Span[] = [];
  private t0 = process.hrtime.bigint();

  start(name: string) {
    this.spans.push({ name, start: process.hrtime.bigint() });
  }

  end(name: string) {
    for (let i = this.spans.length - 1; i >= 0; i -= 1) {
      const s = this.spans[i];
      if (s.name === name && !s.end) {
        s.end = process.hrtime.bigint();
        break;
      }
    }
  }

  private toMs(ns: bigint) {
    return Number(ns) / 1e6;
  }

  header(): string {
    const parts: string[] = [];
    for (const s of this.spans) {
      if (!s.end) continue;
      const dur = this.toMs(s.end - s.start).toFixed(1);
      // Only allow token characters in name for Server-Timing
      const token = s.name.replace(/[^!#$%&'*+.^_`|~0-9A-Za-z-]/g, '-');
      parts.push(`${token};dur=${dur}`);
    }
    const total = this.toMs(process.hrtime.bigint() - this.t0).toFixed(1);
    parts.push(`total;dur=${total}`);
    return parts.join(', ');
  }

  summary(): Record<string, number> {
    const out: Record<string, number> = {};
    for (const s of this.spans) {
      if (!s.end) continue;
      out[s.name] = this.toMs(s.end - s.start);
    }
    out.total = this.toMs(process.hrtime.bigint() - this.t0);
    return out;
  }
}

// Helper to profile a synchronous SSR render block or function. Safe to call on client (no-op).
export function profileSsr<T>(
  timer: SsrTimer | null | undefined,
  name: string,
  fn: () => T
): T {
  try {
    timer?.start(name);
    return fn();
  } finally {
    timer?.end(name);
  }
}
